// Multipart upload of one File straight to R2 through the three
// /s3/multipart_presign endpoints (S3MultipartPresignsController):
//
//   1. POST create  → upload id, fixed part size, concurrency, one presigned
//                     URL per part, and a signed token for the next two calls
//   2. PUT each part to R2 in parallel (XHR, so progress is byte-accurate);
//      a failed part is retried on its own with exponential backoff
//   3. POST complete {token} → the Shrine cached-file JSON the app expects
//
// `complete` is idempotent on the server, so it is retried on network errors
// and 5xx: a lost response does not lose the upload. Small files never come
// here; presign_upload.js picks this path above MULTIPART_THRESHOLD_BYTES.
import { csrfToken } from "../../util/methods"
import {
  xhrPut,
  uploadError,
  abortError,
  isAbortError,
  isRetryableStatus,
  backoffMs,
  delay,
} from "./xhr"

export var MULTIPART_PATHS = {
  create: "/s3/multipart_presign",
  complete: "/s3/multipart_presign/complete",
  abort: "/s3/multipart_presign/abort",
}

var DEFAULT_MAX_PART_ATTEMPTS = 6
var DEFAULT_MAX_COMPLETE_ATTEMPTS = 8

// POST JSON to Rails. Always resolves with { ok, status, json }; a network
// failure or a cancelled request is { ok: false, status: 0, json: null }, and
// callers check their abort state before treating that as retryable.
export function postJson(path, body, signal) {
  return fetch(path, {
    method: "POST",
    credentials: "same-origin",
    signal: signal,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    body: JSON.stringify(body),
  }).then(
    function (response) {
      return response.text().then(
        function (text) {
          var json = null
          if (text) {
            try {
              json = JSON.parse(text)
            } catch {
              json = null
            }
          }
          return { ok: response.ok, status: response.status, json: json }
        },
        function () {
          return { ok: false, status: 0, json: null }
        }
      )
    },
    function () {
      return { ok: false, status: 0, json: null }
    }
  )
}

function errorCode(res) {
  return res.json && res.json.error ? res.json.error.code : undefined
}

function errorMessage(res, fallback) {
  var message = res.json && res.json.error ? res.json.error.message : null
  return message || fallback + " (status " + res.status + ")"
}

// Options:
//   onProgress(loadedBytes, totalBytes)  monotonic; never exceeds file.size
//   signal                               an AbortSignal; cancels the create and
//                                        complete requests, every part, and any
//                                        retry backoff in progress
//   stallMs                              per-part stall detector (see xhr.js)
//   maxPartAttempts / maxCompleteAttempts
// Rejects with an Error carrying `.code`:
//   aborted (name "AbortError") | multipart_unavailable | too_large |
//   presign_failed | network | part_failed | complete_failed
export function multipartUpload(file, options) {
  options = options || {}
  var onProgress = options.onProgress || function () {}
  var signal = options.signal
  var maxPartAttempts = options.maxPartAttempts || DEFAULT_MAX_PART_ATTEMPTS
  var maxCompleteAttempts =
    options.maxCompleteAttempts || DEFAULT_MAX_COMPLETE_ATTEMPTS

  if (signal && signal.aborted) return Promise.reject(abortError())

  var startedAt = Date.now()
  var telemetry = { retry_count: 0, failed_part_attempts: 0 }
  var inflight = []
  var token = null
  var aborted = false
  var fatal = null
  var lastReported = 0

  function telemetryBody(reason) {
    var body = {
      retry_count: telemetry.retry_count,
      failed_part_attempts: telemetry.failed_part_attempts,
      elapsed_ms: Date.now() - startedAt,
    }
    if (reason) body.reason = reason
    return body
  }

  // Best effort: the server also auto-aborts after R2's 7-day window.
  function sendAbort(reason) {
    if (!token) return
    postJson(MULTIPART_PATHS.abort, {
      token: token,
      telemetry: telemetryBody(reason),
    })
  }

  function register(xhr) {
    inflight.push(xhr)
  }

  function unregister(xhr) {
    var index = inflight.indexOf(xhr)
    if (index !== -1) inflight.splice(index, 1)
  }

  function cancelInflight() {
    var pending = inflight.slice()
    inflight = []
    pending.forEach(function (xhr) {
      xhr.abort()
    })
  }

  function onAbort() {
    if (aborted) return
    aborted = true
    cancelInflight()
    sendAbort("user_cancel")
  }

  if (signal) signal.addEventListener("abort", onAbort)

  function cleanup() {
    if (signal) signal.removeEventListener("abort", onAbort)
  }

  function failIfStopped() {
    if (aborted) throw abortError()
    if (fatal) throw fatal
  }

  function startUpload() {
    return postJson(
      MULTIPART_PATHS.create,
      {
        filename: file.name,
        type: file.type || "application/octet-stream",
        size: file.size,
      },
      signal
    ).then(function (res) {
      if (res.ok && res.json && res.json.urls && res.json.token) {
        token = res.json.token
        if (aborted) {
          // Cancelled after create answered: release the upload R2 just opened.
          sendAbort("user_cancel")
          throw abortError()
        }
        return res.json
      }
      // A create cut off by cancel never yields a token; R2's 7-day cleanup
      // covers that upload.
      failIfStopped()
      var code = errorCode(res)
      if (code === "multipart_disabled" || code === "multipart_not_available") {
        throw uploadError(
          "multipart_unavailable",
          errorMessage(res, "Multipart upload unavailable"),
          res.status
        )
      }
      if (code === "too_large") {
        throw uploadError(
          "too_large",
          errorMessage(res, "File too large"),
          res.status
        )
      }
      if (res.status === 0) {
        throw uploadError(
          "network",
          "Could not reach the server to start the upload",
          0
        )
      }
      throw uploadError(
        "presign_failed",
        errorMessage(res, "Could not start the upload"),
        res.status
      )
    })
  }

  function uploadParts(created) {
    var partCount = created.part_count
    var partSize = created.part_size
    var workers = Math.max(1, Math.min(created.concurrency || 1, partCount))
    var loadedByPart = []
    for (var i = 0; i < partCount; i++) loadedByPart.push(0)
    var next = 0

    // Byte accounting stays exact (a retried part drops back to 0), but what
    // FilePond sees only ever grows.
    function report() {
      var sum = 0
      for (var j = 0; j < loadedByPart.length; j++) sum += loadedByPart[j]
      var value = Math.min(sum, file.size)
      if (value > lastReported) {
        lastReported = value
        onProgress(value, file.size)
      }
    }

    function putPart(index, attempt) {
      var start = index * partSize
      // A slice is a lazy reference into the File; nothing is read into memory here.
      var blob = file.slice(start, Math.min(start + partSize, file.size))
      return xhrPut(created.urls[index], {
        body: blob,
        signal: signal,
        stallMs: options.stallMs,
        register: register,
        unregister: unregister,
        onProgress: function (loaded) {
          loadedByPart[index] = Math.min(loaded, blob.size)
          report()
        },
      }).then(
        function () {
          loadedByPart[index] = blob.size
          report()
        },
        function (err) {
          failIfStopped()
          loadedByPart[index] = 0
          telemetry.failed_part_attempts += 1
          if (isRetryableStatus(err.status) && attempt + 1 < maxPartAttempts) {
            telemetry.retry_count += 1
            return delay(backoffMs(attempt), signal).then(function () {
              failIfStopped()
              return putPart(index, attempt + 1)
            })
          }
          throw uploadError(
            "part_failed",
            "Part " +
              (index + 1) +
              " of " +
              partCount +
              " failed (status " +
              err.status +
              ")",
            err.status
          )
        }
      )
    }

    // Each worker takes the next part number until none are left. The first
    // final failure stops every sibling.
    function worker() {
      if (aborted || fatal || next >= partCount) return Promise.resolve()
      var index = next++
      return putPart(index, 0).then(worker, function (err) {
        if (!fatal && !aborted) {
          fatal = err
          cancelInflight()
        }
        throw err
      })
    }

    var runners = []
    for (var w = 0; w < workers; w++) runners.push(worker())
    return Promise.all(runners)
  }

  function completeUpload(attempt) {
    return postJson(
      MULTIPART_PATHS.complete,
      { token: token, telemetry: telemetryBody() },
      signal
    ).then(function (res) {
      failIfStopped()
      if (res.ok && res.json && res.json.id) return res.json
      if (isRetryableStatus(res.status) && attempt + 1 < maxCompleteAttempts) {
        telemetry.retry_count += 1
        return delay(backoffMs(attempt), signal).then(function () {
          failIfStopped()
          return completeUpload(attempt + 1)
        })
      }
      throw uploadError(
        "complete_failed",
        errorMessage(res, "Could not finish the upload"),
        res.status
      )
    })
  }

  return startUpload()
    .then(uploadParts)
    .then(function () {
      failIfStopped()
      return completeUpload(0)
    })
    .then(
      function (cached) {
        cleanup()
        return cached
      },
      function (err) {
        cleanup()
        // A 422/410 from complete means the server already aborted or the
        // upload is gone. Anything else (network, 5xx retries exhausted) may
        // leave the upload parked, so send a best-effort abort; if the object
        // was in fact completed, abort is a no-op on R2.
        var serverHandled =
          err.code === "complete_failed" && !isRetryableStatus(err.status)
        if (!aborted && !isAbortError(err) && !serverHandled) {
          sendAbort(err.code || "error")
        }
        throw err
      }
    )
}
